import { Component, input, inject, output, signal, effect, computed } from '@angular/core';
import {
  Role,
  RoleToStringMapping,
  StringToRoleMapping,
  Tool,
  UserInfo,
} from '../../utils/models/tool.models';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent, CuiDialogService } from '@innersource/common-ui-components';
import { ToolService } from '../../services/api-services/tools/tool.service';
import { UserService } from '../../services/api-services/user.service';
import {
  FormControl,
  FormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { NotificationDialComponent } from '../../utils/dialogues/notification-dial/notification-dial.component';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/autocomplete';
import { MatButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { RegistrationListComponent } from '../admin-area-list/registration-list/registration-list.component';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-user-config',
  templateUrl: './user-config.component.html',
  styleUrls: ['./user-config.component.scss'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatSelect,
    MatOption,
    MatButton,
    MatCheckbox,
    RegistrationListComponent,
  ],
})
export class UserConfigComponent {
  /** Inject services. */
  private readonly dialogRef = inject(MatDialog);
  private readonly confirmationDialog = inject(CuiDialogService);
  private readonly toolService = inject(ToolService);
  private readonly userService = inject(UserService);

  readonly userId = input<number | undefined>();
  readonly tools = input<Tool[]>();
  readonly applyEvent = output();

  readonly users = this.userService.getAllUsers();
  readonly registrations = this.toolService.getRegistrations();
  readonly user = computed(() => {
    const users = this.users.value();
    const userId = this.userId();
    const registrations = this.registrations.value() ?? [];
    if (!users || !userId) return undefined;
    const tools = registrations.filter(x => x.user.userId == userId).map(r => r.tool);
    for (const user of users) {
      if (user.userId == userId) return { isNew: false, user, tools };
    }
    const newUser: UserInfo = {
      userId,
      role: Role.User,
      userName: '',
      canAccessChat: true,
    };
    return { isNew: true, user: newUser, tools };
  });

  userDataForm = new FormGroup({
    userId: new FormControl('', [Validators.required]),
    userName: new FormControl('', [Validators.required]),
    role: new FormControl('', [Validators.required]),
    canAccessChat: new FormControl(true, { nonNullable: true }),
  });

  selectedTools = signal<number[]>([]);

  roles = Object.values(RoleToStringMapping);

  /** Constructor. */
  constructor() {
    effect(() => {
      const user = this.user()?.user;
      if (!user) return;
      this.userDataForm.controls['userId'].setValue(user.userId.toString());
      this.userDataForm.controls['role'].setValue(RoleToStringMapping[user.role]);
      this.userDataForm.controls['userName'].setValue(user.userName);
      this.userDataForm.controls['canAccessChat'].setValue(user.canAccessChat);
    });
  }

  async apply() {
    await this.updateUserData();
    await this.updateToolRegistration();
    this.applyEvent.emit();
  }

  async updateUserData() {
    const newUserName = this.userDataForm.get('userName')?.value;
    const userRole = this.userDataForm.get('role')?.value;
    const canAccessChat = this.userDataForm.controls.canAccessChat.value;

    if (newUserName && userRole) {
      const user = this.user()?.user;
      if (
        newUserName !== user?.userName ||
        StringToRoleMapping[userRole] !== user?.role ||
        canAccessChat !== user?.canAccessChat
      ) {
        if (this.user()?.isNew) {
          const user = await this.userService.createUser(
            newUserName,
            StringToRoleMapping[userRole],
            canAccessChat
          );
          const ref = this.dialogRef.open(NotificationDialComponent, {
            data: {
              title: 'New User created',
              message: `The new user has been created with id ${user.userId}. The password was set to ${user.pwd}`,
            },
          });
          await firstValueFrom(ref.afterClosed());
        } else {
          const userId = this.userDataForm.get('userId')?.value;
          if (userId) {
            await this.userService.updateUserInternal({
              userId: Number(this.userDataForm.get('userId')?.value),
              userName: newUserName,
              role: StringToRoleMapping[userRole],
              canAccessChat,
            });
          }
        }
      }
    }
  }

  async updateToolRegistration() {
    const tools = this.tools();
    const user = this.user();
    if (!tools || !user) return;

    const promises: Promise<string>[] = [];
    // Search for tools which need to be added
    for (const toolId of this.selectedTools()) {
      if (!user.tools.some(el => el.toolId === toolId)) {
        promises.push(this.toolService.updateRegistration(user.user.userId, toolId));
      }
    }

    // Search for tools which need to be removed
    for (const tool of tools) {
      if (
        !this.selectedTools().some(el => el === tool.toolId) &&
        user.tools.some(el => el.toolId === tool.toolId)
      ) {
        promises.push(this.toolService.deleteRegistration(user.user.userId, tool.toolId));
      }
    }

    await Promise.all(promises);
  }

  async triggerPwdGeneration() {
    const userId = this.userDataForm.get('userId')?.value;
    if (userId) {
      const res = await this.userService.resetUserPassword(Number(userId));
      if (res.success) {
        this.dialogRef.open(NotificationDialComponent, {
          data: {
            title: 'Password reset successfully',
            message: res.msg,
          },
        });
      } else {
        this.dialogRef.open(NotificationDialComponent, {
          data: {
            title: 'Failed to reset password!',
            message: res.msg,
          },
        });
      }
    }
  }

  openChangePassword() {
    this.confirmationDialog.open(ConfirmationDialogComponent, {
      size: 'small',
      ariaLabel: 'Generate new password',
      minWidth: '24rem',
      data: {
        confirmTitle: 'Generate new password',
        confirmMessage:
          'Do you really want to generate a new password? ' +
          'The old password will be overwritten and the user can only login with new password.',
        onConfirm: () => this.triggerPwdGeneration(),
      },
    });
  }
}
